"use client";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import {
  ANALYTICS_EVENT_ACTION_ADDED,
  ANALYTICS_EVENT_ACTION_EDITED,
} from "@stadium/services/analytics/constants/default";
import { ADDRESS_TARGET } from "@stadium/checkout/src/constants/analytics";
import omit from "lodash/omit";
import pick from "lodash/pick";
import debounce from "lodash/debounce";
import compact from "lodash/compact";
import SmartyStreetService from "@stadium/services/smartystreets";
import SuggestionsDropdown from "@stadium/ui/src/components/address/suggestions-dropdown";
import Button from "@stadium/checkout/src/components/shared/buttons";
import Input from "@stadium/ui/src/components/input/input";
import ErrorMsg from "@stadium/ui/src/components/error-message/error-message";
import CountryFlag from "@stadium/ui/src/shared/country-flag";

import { SelectDropdown } from "@sendmagic/prismic-nextjs/src/components/utils/SelectDropdown";

import AddressConfirmationModal from "@stadium/checkout/src/components/shipping-address/address-confirmation/confirmation-modal";

import { selectAllCountries } from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { useFlags } from "@stadium/services/launch-darkly";
import { addressFormNoTranslationFlag } from "@stadium/services/launch-darkly/constants";

import { normalizePhone, normalizeZip } from "@stadium/helpers/form/normalizer";
import { Validator } from "@stadium/helpers/form/validation";
import {
  getOrderedStateOptions,
  getCountryDetailsUsingIso,
  getCountryDetailsUsingId,
  isGlobalCountry,
} from "@stadium/helpers/country/utils";
import { getSelectDropdownMapping } from "@stadium/helpers/form/utils";
import { transformObject } from "@stadium/helpers/formatter";
import { removeEmoticonsFromStr } from "@stadium/helpers/emoticons";
import {
  formatCountryForAddress,
  initializeFormValues,
} from "@stadium/checkout/src/helpers/address";

import { US_ISO } from "@stadium/constants/country";
import NON_INTERNATIONAL_STATES from "@stadium/constants/non-international-states";
import { weglotNoTranslationClassName } from "@stadium/constants";
import {
  COUNTRY_ID,
  PHONE,
  FORMATTED_ADDRESS,
  COMPANY_NAME,
  SUGGESTIONS_DROPDOWN,
  STATE_NAME,
  ZIPCODE,
  ZIPCODE_NON_US,
  NEW_DEFAULT_FORM_FIELDS,
} from "constants/form";

import styles from "./main.module.scss";

const getCountryByText = ({ iso, name }) => (
  <span className={`${styles.countryGroup}`}>
    <CountryFlag country={{ iso, name }} type="round" />
    <span className={`${styles.countryName}`}>{name}</span>
  </span>
);

const DEFAULT_OBJ = {};

const AddressForm = ({
  addressObj,
  onAddAddress,
  isCountryEditable = false,
  defaultCountry,
  renderCustomAddressForm,
  formFields: formFieldsFromProps,
  hideCountryFlag = false,
  eventLocationAnalytics = DEFAULT_OBJ,
  ...rest
}) => {
  const formFields = formFieldsFromProps ?? NEW_DEFAULT_FORM_FIELDS;

  const countries = useConfig(selectAllCountries);
  const [selectedCountry, setSelectedCountry] = useState({});

  const withAnalytics = useWithAnalytics();

  const { countryId, countryIso, countryName, countryDialCode, isInternational } =
    !isCountryEditable && rest.countryId ? rest : selectedCountry;

  const { [addressFormNoTranslationFlag]: addressFormNoTranslationCountries = "all" } = useFlags();

  const shouldApplyNoTranslationClass = useMemo(() => {
    const raw = addressFormNoTranslationCountries?.trim();
    if (raw === "all") return true;
    if (!raw || !countryIso) return false;

    const allowedIsos = raw
      .split(",")
      .map((iso) => iso.trim().toUpperCase())
      .filter(Boolean);

    return allowedIsos.includes(countryIso.toUpperCase());
  }, [addressFormNoTranslationCountries, countryIso]);

  const updatedAddressObj = !isInternational
    ? { ...addressObj, stateName: addressObj.stateAbbreviation }
    : addressObj;

  const [formValues, setFormValues] = useState(() =>
    initializeFormValues(updatedAddressObj, isInternational),
  );

  useEffect(() => {
    const countryDependentFields = ["stateName", "stateNameNonUs", "zipcode", "zipcodeNonUs"];
    const newFormValues = initializeFormValues(updatedAddressObj, isInternational);

    setFormValues((prev) => ({
      ...prev,
      ...pick(newFormValues, countryDependentFields),
    }));

    setFormErrors((prev) => omit(prev, countryDependentFields));
  }, [selectedCountry]);

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressConfirmationParams, setAddressConfirmationParams] = useState({});
  const [showAddressConfirmationModal, setShowAddressConfirmationModal] = useState(false);

  const openAddressConfirmationModal = () => {
    const addressFormModalEle = document?.querySelector('[data-id="address-form-modal"]');
    //Closing address form modal which opens confirmation modal
    if (addressFormModalEle) addressFormModalEle.style.display = "none";
    setShowAddressConfirmationModal(true);
  };
  const closeAddressConfirmationModal = () => {
    const addressFormModalEle = document?.querySelector('[data-id="address-form-modal"]');
    if (addressFormModalEle) addressFormModalEle.style.display = "block";
    setShowAddressConfirmationModal(false);
  };

  const address2InputFieldRef = useRef(null);
  const autocompleteAddress = countryIso === US_ISO;

  useEffect(() => {
    if (autocompleteAddress) {
      SmartyStreetService.initialize({ apiKey: process.env.NEXT_PUBLIC_SMARTY_STREET_WEBSITE_KEY });
    }
  }, [autocompleteAddress]);

  const updateCountry = (country) => {
    const formattedCountry = formatCountryForAddress(country);
    setSelectedCountry(formattedCountry);
  };

  useEffect(() => {
    if (addressObj?.countryId) {
      const countryData = getCountryDetailsUsingId(countries, addressObj?.countryId);
      updateCountry(countryData);
    } else {
      const isDefaultGlobal = isGlobalCountry(defaultCountry);
      const countryISO = isDefaultGlobal ? US_ISO : defaultCountry?.iso;
      const selectedCountryISO = countryISO || US_ISO;
      const countryData = getCountryDetailsUsingIso(countries, selectedCountryISO);
      updateCountry(countryData);
    }
  }, [defaultCountry, addressObj]);

  const openAddressConfirmation = (params) => {
    setAddressConfirmationParams(params);
    openAddressConfirmationModal();
  };

  const getFormValuesForRequest = (data = formValues) =>
    transformObject(
      data,
      (key) => key,
      ({ value }) => value,
    );

  const querySmartyStreetAutoComplete = useCallback(
    debounce(
      (value) => {
        const successCb = (suggestions = []) => {
          const addressSuggestion = suggestions.map((suggestion) => {
            const { streetLine, city, text, state, secondary } = suggestion;
            return {
              value: suggestion,
              text: text || compact([secondary, streetLine, city, state]).join(", "),
            };
          });
          setAddressSuggestions(addressSuggestion);
          setShowSuggestions(true);
        };

        // To make optimized calls for Smarty, a character length check is added
        // so as to trigger lookups only when this length is reached
        // while searching for an address
        if (value.length >= 10) {
          SmartyStreetService.queryAutocompleteForSuggestions(value).then(successCb);
        } else {
          setAddressSuggestions([]);
          setShowSuggestions(false);
        }
      },
      500,
      {
        leading: false,
        trailing: true,
      },
    ),
    [],
  );

  const isSameAsUserAddress = (validAddress) =>
    Object.keys(validAddress).every((field) => validAddress[field] === formValues[field].value);

  const validateAddress = () => {
    // skip smartystreet for non-US addresses
    if (!autocompleteAddress) {
      return { type: "verified", validAddress: getFormValuesForRequest() };
    }

    const validAddCb = (validationData, validAddress) => {
      const isValidAddressSameAsUserAddress = isSameAsUserAddress(validAddress);
      const updatedFormValues = {
        ...formValues,
        validationData: {
          ...formValues.validationData,
          value: validationData,
        },
      };
      setFormValues(updatedFormValues);
      setFormErrors({});
      setIsSubmitting(isValidAddressSameAsUserAddress);
      const userSubmittedAddress = getFormValuesForRequest(updatedFormValues);
      const updatedValidAddress = {
        ...userSubmittedAddress,
        ...validAddress,
        verified: true,
      };
      if (isValidAddressSameAsUserAddress) {
        return { type: "verified", validAddress: updatedValidAddress };
      }

      return {
        type: "valid",
        invalidAddress: userSubmittedAddress,
        validAddress: updatedValidAddress,
      };
    };

    const invalidAddCb = (validationData) => {
      const updatedFormValues = {
        ...formValues,
        validationData: {
          ...formValues.validationData,
          value: validationData,
        },
      };
      setFormValues(updatedFormValues);
      setFormErrors({});
      setIsSubmitting(false);
      const userSubmittedAddress = getFormValuesForRequest(updatedFormValues);
      return {
        invalidAddress: userSubmittedAddress,
        type: "invalid",
      };
    };

    const errorCb = () => {
      setFormErrors({});
      setIsSubmitting(false);
      const userSubmittedAddress = getFormValuesForRequest();

      return {
        invalidAddress: userSubmittedAddress,
        type: "invalid",
      };
    };

    return SmartyStreetService.validate(getFormValuesForRequest())
      .then(({ validAddress, isValid, response }) => {
        if (isValid) {
          return validAddCb(response, validAddress);
        } else {
          return invalidAddCb(response);
        }
      })
      .catch(errorCb);
  };

  const isValidAddress = () => {
    let errors = {};
    Object.entries(formValues).forEach(([field, value]) => {
      const err = Validator(value, formValues);
      if (err.length) {
        errors = { ...errors, [field]: err[0] };
      }
    });

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return false;
    }

    return true;
  };

  const handleValidateFormError = (errors) => {
    setFormErrors(errors);

    setIsSubmitting(false);
  };

  const validateFormAndAddress = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!isValidAddress()) {
      return;
    }

    const { type, validAddress, invalidAddress } = await validateAddress();

    const isUpdating = !!addressObj?.externalId;
    const action = isUpdating ? ANALYTICS_EVENT_ACTION_EDITED : ANALYTICS_EVENT_ACTION_ADDED;

    const wrappedOnAddAddress = eventLocationAnalytics?.track
      ? withAnalytics(onAddAddress, {
          ...eventLocationAnalytics,
          track: true,
          target: eventLocationAnalytics.target ?? ADDRESS_TARGET,
          action: eventLocationAnalytics.action ?? action,
        })
      : onAddAddress;

    if (type === "verified") {
      const payload = { ...validAddress, verified: true, countryId };

      return wrappedOnAddAddress(payload, handleValidateFormError);
    }

    return openAddressConfirmation({
      type,
      validAddress,
      invalidAddress,
      onAddAddress: wrappedOnAddAddress,
      handleValidateFormError,
    });
  };

  const handleShowSuggestions = (value = false) => {
    setShowSuggestions(value);
  };

  const onSuggestionSelect = (suggestion) => {
    if (!suggestion) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const { streetLine, secondary = "", city, state, zipcode } = suggestion;
    const updatedValues = {
      address1: streetLine,
      address2: secondary,
      city,
      stateName: state,
      zipcode: normalizeZip(zipcode, { country: countryIso, isInternational }),
      formattedAddress: streetLine,
    };
    let updatedFormValues = { ...formValues };

    Object.entries(updatedValues).forEach(([key, value]) => {
      if (key === "stateName") {
        const stateAbbr = getSelectDropdownMapping({
          data: NON_INTERNATIONAL_STATES[countryIso],
          keyAttr: "abbr",
        })?.[value]?.abbr;

        updatedFormValues = {
          ...updatedFormValues,
          stateName: {
            ...updatedFormValues.stateName,
            value,
            text: stateAbbr || "",
          },
        };
      }
      updatedFormValues = {
        ...updatedFormValues,
        [key]: { ...formValues[key], value },
      };
    });
    setFormValues(updatedFormValues);
    setFormErrors(
      omit(formErrors, [
        "address1",
        "address2",
        "city",
        "stateName",
        "zipcode",
        "formattedAddress",
      ]),
    );
    setAddressSuggestions([]);
    setShowSuggestions(false);
    address2InputFieldRef.current?.focus();
  };

  const stateOptions = useMemo(
    () => getOrderedStateOptions({ data: NON_INTERNATIONAL_STATES[countryIso] }),
    [countryIso],
  );

  const countriesOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country.iso,
        text: hideCountryFlag ? country?.name : getCountryByText(country),
        name: country.name,
      })),
    [countries, hideCountryFlag],
  );

  const fieldAttributes = (fieldName) => {
    if (fieldName == ZIPCODE.name) {
      fieldName = isInternational ? ZIPCODE_NON_US.name : fieldName;
    }
    const attributes = {
      name: fieldName,
      id: fieldName,
      value: formValues[fieldName]?.value,
      error: !!formErrors[fieldName],
    };

    switch (fieldName) {
      case PHONE.name:
        return {
          ...attributes,
          validate: ({ name, value }) => ({
            name,
            value: normalizePhone(value, {
              showBrackets: false,
              isInternational,
            }),
          }),
          prefix: <span className={`${styles.countryCode}`}>{countryDialCode}</span>,
        };
      case ZIPCODE.name:
        return {
          ...attributes,
          validate: ({ name, value }) => ({
            name,
            value: normalizeZip(value, { country: countryIso, isInternational }),
          }),
        };
      case SUGGESTIONS_DROPDOWN.name:
        return {
          suggestions: addressSuggestions,
          onSelect: onSuggestionSelect,
          active: showSuggestions,
          toggle: handleShowSuggestions,
        };
      case FORMATTED_ADDRESS.name:
        return {
          ...attributes,
          autoComplete: "off",
          type: "search",
          onChange: ({ name, value }) => {
            handleFieldChange({ name, value });
            if (autocompleteAddress) {
              querySmartyStreetAutoComplete(value);
            }
          },
        };
      case COUNTRY_ID.name: {
        return {
          ...attributes,
          onSelect: ({ value }) => {
            updateCountry(countries.find(({ iso }) => iso === value));
          },
          showSearchFilter: true,
          searchfilterOptions: {
            placeholder: "Search",
            attributes: ["iso", "name"],
          },
          text: hideCountryFlag
            ? countryName
            : getCountryByText({ iso: countryIso, name: countryName }),
        };
      }
      default:
        return attributes;
    }
  };

  const handleFieldChange = ({ name, value }) => {
    const updatedValue = typeof value === "string" ? removeEmoticonsFromStr(value) : value;
    let updatedFormValues = { ...formValues, [name]: { ...formValues[name], value: updatedValue } };
    let updatedFormErrors = omit(formErrors, [name]);
    if (name === "formattedAddress") {
      // address1 is set only when a suggestion is selected,
      // but in case user doesn't selects any address it is assigned the same value as formattedAddress
      updatedFormValues = {
        ...updatedFormValues,
        address1: { ...formValues.address1, value: updatedValue },
      };
    }

    setFormValues(updatedFormValues);
    setFormErrors(updatedFormErrors);
  };

  const STYLES_MAPPING = {
    [FORMATTED_ADDRESS.name]: styles.addressInput,
    [COMPANY_NAME.name]: styles.companyName,
  };

  const getFormFieldClass = (name) => {
    return formErrors[name] ? "input-error" : " ";
  };

  const renderCountry = ({ name }) => {
    return (
      <div className={styles.formCol}>
        <div className={styles.countryField}>
          <label htmlFor={name} className={styles.labelName}>
            Country
          </label>
          {isCountryEditable ? (
            <SelectDropdown
              options={countriesOptions}
              {...fieldAttributes(COUNTRY_ID.name)}
              value={countryIso}
              responsive
            />
          ) : (
            <Button
              {...fieldAttributes(name)}
              disabled
              className={`${styles.formControl} ${styles.formControlBtn} ${getFormFieldClass(
                "countryId",
              )}`}
              styleType="no-style"
            >
              {fieldAttributes(name).text}
            </Button>
          )}
          {!!formErrors[name] && <ErrorMsg msg={formErrors[name]} />}
        </div>
      </div>
    );
  };
  const renderStateField = ({ name, placeholder, label }) => {
    return (
      <div className={styles.formCol}>
        <div className={styles.formField}>
          {isInternational ? (
            <>
              {" "}
              <Input
                {...fieldAttributes("stateNameNonUs")}
                type="text"
                customClass={`${styles.formControl} ${getFormFieldClass("stateNameNonUs")}`}
                placeholder="NY"
                label={label}
                onChange={handleFieldChange}
                labelNameClass={styles.labelName}
              />
              {!!formErrors.stateNameNonUs && (
                <span className={styles.errorMsg}>
                  <ErrorMsg msg={formErrors.stateNameNonUs} />
                </span>
              )}
            </>
          ) : (
            <>
              <label htmlFor={name} className={styles.labelName}>
                {label}
              </label>
              <SelectDropdown
                placeholder={placeholder}
                options={stateOptions}
                {...fieldAttributes(name)}
                onSelect={handleFieldChange}
                showSearchFilter
                text={formValues.stateName.value}
                searchfilterOptions={{
                  placeholder: "Search",
                  attributes: ["abbr", "name"],
                }}
                classes={getFormFieldClass("stateName")}
                responsive
              />
              {!!formErrors[name] && (
                <span className={styles.errorMsg}>
                  <ErrorMsg msg={formErrors[name]} />
                </span>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <form
      id="address-form-component"
      onSubmit={validateFormAndAddress}
      className={shouldApplyNoTranslationClass ? weglotNoTranslationClassName : undefined}
    >
      <div className={styles.formRow}>
        {formFields.map((formField) => {
          if (formField.name == COUNTRY_ID.name) {
            return renderCountry(formField);
          }
          if (formField.name == STATE_NAME.name) {
            return renderStateField(formField);
          }
          const { name, placeholder, label, type } = formField;
          return (
            <div className={`${STYLES_MAPPING[name] || styles.formCol}`} key={name}>
              <div className={styles.formField}>
                <Input
                  onChange={handleFieldChange}
                  {...fieldAttributes(name)}
                  type={type}
                  customClass={styles.formControl}
                  placeholder={placeholder}
                  label={label}
                  labelNameClass={styles.labelName}
                />
                {!!formErrors[name] && (
                  <span className={styles.errorMsg}>
                    <ErrorMsg msg={formErrors[name]} />
                  </span>
                )}

                {name == FORMATTED_ADDRESS.name && autocompleteAddress && (
                  <div className={`form-dropdown ${styles.suggestions}`}>
                    <SuggestionsDropdown
                      {...fieldAttributes(SUGGESTIONS_DROPDOWN.name)}
                      inputFieldClass={"suggestionsInputField"}
                      styles={styles}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </form>
  );

  const notVerifiedAddressModalProps = {
    isInternational,
    countryId,
    ...addressConfirmationParams,
    countryName,
    closeModal: closeAddressConfirmationModal,
  };

  const renderAddressNotVerifiedModal = () =>
    showAddressConfirmationModal && <AddressConfirmationModal {...notVerifiedAddressModalProps} />;

  if (typeof renderCustomAddressForm === "function") {
    return renderCustomAddressForm({
      renderForm,
      addressNotVerified: showAddressConfirmationModal,
      notVerifiedAddressModalProps,
      renderAddressNotVerifiedModal,
      onSubmit: validateFormAndAddress,
      isSubmitting,
    });
  }

  return (
    <>
      <article className={styles.shipCardBody}>{renderForm()}</article>
      <div
        className={`${styles.shipCardFooter} ${
          shouldApplyNoTranslationClass ? weglotNoTranslationClassName : ""
        }`}
      >
        <Button
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
          fillStroke="#000"
          type="submit"
          form="address-form-component"
          className={styles.btnSave}
          styleType="primary"
        >
          Save
        </Button>
      </div>
      {shouldApplyNoTranslationClass && (
        <div className={`${styles.noteMessage} ${weglotNoTranslationClassName}`}>
          <strong>Note:</strong> Please add address in English only.
        </div>
      )}
      {renderAddressNotVerifiedModal()}
    </>
  );
};

export default AddressForm;
